import React, { useEffect, useState } from 'react';

// Official blind churn trials, as recorded in the Kizashi feature log (docs/features.md and
// docs/yocho-trials/). Trials 1-3 are single worlds (about 6 churners, so wide uncertainty and
// no interval recorded); 4 and 5a pool 5 worlds with bootstrap 95% intervals.
export const TRIALS = [
  { id: '#1', auc: 0.438, ci: null, worlds: 1, what: 'Health score v1, no ERP/AR or text signals yet' },
  { id: '#2', auc: 0.81, ci: null, worlds: 1, what: 'v1 plus ERP/AR order and billing signals' },
  { id: '#3', auc: 0.494, ci: null, worlds: 1, what: 'Every input fed, relative alert band' },
  { id: '#4', auc: 0.834, ci: [0.746, 0.912], worlds: 5, what: 'Health score v2 with per-entity baselines' },
  { id: '#5a', auc: 0.866, ci: [0.8, 0.92], worlds: 5, what: 'v2 with trajectory alerts' },
];

export default function TrialChart() {
  const [hover, setHover] = useState(null);
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => setNarrow(mq.matches);
    on(); mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const W = narrow ? 340 : 560; const H = narrow ? 240 : 250;
  const m = { l: 36, r: narrow ? 8 : 16, t: 16, b: 40 };
  const iw = W - m.l - m.r; const ih = H - m.t - m.b;
  const x = (i) => m.l + ((i + 0.5) * iw) / TRIALS.length;
  const y = (v) => m.t + ih - ((v - 0.3) / 0.7) * ih;
  const ticks = [0.3, 0.5, 0.7, 0.9, 1.0];
  const h = hover != null ? TRIALS[hover] : null;

  return (
    <figure className="kz-chart">
      <figcaption className="kz-chart-title">ROC-AUC per official blind trial</figcaption>
      <div className="kz-chart-plot">
        <svg viewBox={`0 0 ${W} ${H}`} role="img"
          aria-label="ROC-AUC by trial: #1 0.438, #2 0.810, #3 0.494, #4 0.834 (0.746 to 0.912), #5a 0.866 (0.800 to 0.920)">
          {ticks.map((t) => (
            <g key={t}>
              <line x1={m.l} x2={W - m.r} y1={y(t)} y2={y(t)} className={t === 0.5 ? 'kz-ax-chance' : 'kz-ax-grid'} />
              <text x={m.l - 8} y={y(t) + 4} className="kz-ax-label" textAnchor="end">{t.toFixed(1)}</text>
            </g>
          ))}
          <text x={W - m.r} y={y(0.5) - 6} className="kz-ax-note" textAnchor="end">chance (0.5)</text>
          {TRIALS.map((d, i) => (
            <g key={d.id} className={`kz-pt ${hover === i ? 'is-on' : ''}`}
              tabIndex={0} role="button" aria-label={`Trial ${d.id}: ROC-AUC ${d.auc.toFixed(3)}`}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)} onBlur={() => setHover(null)}>
              <rect x={x(i) - iw / TRIALS.length / 2} y={m.t} width={iw / TRIALS.length} height={ih} className="kz-pt-hit" />
              {d.ci && (
                <g className="kz-pt-ci">
                  <line x1={x(i)} x2={x(i)} y1={y(d.ci[0])} y2={y(d.ci[1])} />
                  <line x1={x(i) - 6} x2={x(i) + 6} y1={y(d.ci[0])} y2={y(d.ci[0])} />
                  <line x1={x(i) - 6} x2={x(i) + 6} y1={y(d.ci[1])} y2={y(d.ci[1])} />
                </g>
              )}
              <circle cx={x(i)} cy={y(d.auc)} r={d.worlds > 1 ? 6 : 5} className={d.worlds > 1 ? 'kz-pt-dot' : 'kz-pt-dot is-single'} />
              {(i >= 3 && !narrow) && <text x={x(i) + 12} y={y(d.auc) + 4} className="kz-pt-label">{d.auc.toFixed(3)}</text>}
              <text x={x(i)} y={H - m.b + 18} className="kz-ax-label" textAnchor="middle">{d.id}</text>
              <text x={x(i)} y={H - m.b + 32} className="kz-ax-sub" textAnchor="middle">{d.worlds === 1 ? '1 world' : '5 worlds'}</text>
            </g>
          ))}
        </svg>
        {h && (
          <div className="kz-tip" style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(h.auc) / H) * 100}%` }}>
            <b>Trial {h.id}</b> · ROC-AUC {h.auc.toFixed(3)}
            {h.ci && <span> [{h.ci[0].toFixed(3)}–{h.ci[1].toFixed(3)}]</span>}
            <div>{h.what}</div>
          </div>
        )}
      </div>
      <div className="kz-chart-legend">
        <span><i className="lg lg-dot" />pooled over 5 worlds, with 95% bootstrap interval</span>
        <span><i className="lg lg-dot is-single" />single world (about 6 churners, no interval)</span>
      </div>
      <details className="kz-table-view">
        <summary>Show as a table</summary>
        <table>
          <thead><tr><th>Trial</th><th>Worlds</th><th>ROC-AUC</th><th>95% interval</th><th>What changed</th></tr></thead>
          <tbody>
            {TRIALS.map((d) => (
              <tr key={d.id}><td>{d.id}</td><td>{d.worlds}</td><td>{d.auc.toFixed(3)}</td>
                <td>{d.ci ? `${d.ci[0].toFixed(3)}–${d.ci[1].toFixed(3)}` : '—'}</td><td>{d.what}</td></tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
